import { useAuth } from 'context/AuthContext'
import { useState } from 'react'

export const useRequest = <T extends object>(doRequest: () => Promise<T> | undefined) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<HttpError | undefined>()

    const run = async () => {
        try {
            setLoading(true)
            return await doRequest()
        } catch (error: unknown) {
            if (isHttpError(error)) setError(error)
            else throw error
        } finally {
            setLoading(false)
        }
    }

    return {
        run,
        loading,
        error,
    }
}

export const useFetchWithAuth = () => {
    const { getAccessToken } = useAuth()

    return async <T, R>(endpoint: string, method: 'GET' | 'POST' | 'PATCH' = 'GET', body?: T) => {
        const accessToken = await getAccessToken()
        if (!accessToken) throw Error('Access token is undefined')

        return fetcher<T, R>(endpoint, method, body, getHeaders(accessToken))
    }
}

export const fetcher = <T, R>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PATCH' = 'GET',

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    body?: T,
    headers: HeadersInit = { 'Content-Type': 'application/json' }
): Promise<R> => {
    return fetch(endpoint, {
        method,
        headers,
        body: JSON.stringify(body),
    })
        .then(async (response) => {
            if (!response.ok) {
                // eslint-disable-next-line no-throw-literal
                throw { message: 'Request failed', body: await getBody(response), statusCode: response.status }
            }
            return getBody<R>(response) as Promise<R>
        })
        .catch((error) => {
            throw error
        })
}

export interface HttpError {
    statusCode: number
    message: string
    body?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isHttpError = (error: any): error is HttpError => error?.message === 'Request failed'

const getHeaders = (token: string): HeadersInit => {
    return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
    }
}

const getBody = <T = string>(res: Response): Promise<T | undefined> => {
    if (res.headers.get('Content-Type') !== 'application/json') return Promise.resolve(undefined)
    return res.json() as Promise<T>
}
